import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  INJECTOR,
  input,
  linkedSignal,
  OnInit,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  IconComponent,
  modalFn,
  ResolvableModal,
  TranslatePipe,
  translator,
} from '@softline/ui-core';
import { TabComponent, TabGroupComponent } from '@softline/ui-core/tab-group';
import { Bewe, Bewelg, PositionErledigenSplit } from '../../types/bewegung';
import { handleRequestError, MengenEingabeComponent } from '@softline/application';
import { Store } from '@softline/core';
import {
  SOFTLINE_CONFIG_WARENUEBERNAHME,
  SOFTLINE_FEATURE_WWS_WARENUEBERNAHME_BEWE,
} from '../../warenuebernahme.shared';
import { WwsBewegungStore } from '../../store/bewegung.store';
import { Warenuebernahme } from '../../types/warenuebernahme';
import { MischPalettenService } from '../../services/mischpaletten-service';
import { MischPalette } from '../../types/misch-paletten';
import { BewegungenService } from '../../services/bewegungen-service';
import { firstValueFrom } from 'rxjs';
import { PaletteBean } from '../../types/pallet-item';
import { PalletDetailDialogComponent } from '../pallet-detail/pallet-detail-dialog.component';
import { PalletSummaryDialogComponent } from '../pallet-summary/pallet-summary-dialog.component';

@Component({
  selector: 'soft-position-erledigen-dialog',
  imports: [
    CommonModule,
    MengenEingabeComponent,
    IconComponent,
    TabComponent,
    TabGroupComponent,
    TranslatePipe,
  ],
  templateUrl: './position-erledigen-dialog.component.html',
  styleUrls: ['./position-erledigen-dialog.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [MischPalettenService],
})
export class PositionErledigenDialogComponent
  extends ResolvableModal<null | 'kopf' | 'bewegung'>
  implements OnInit
{
  readonly #injector = inject(INJECTOR);

  // public anzahlEtiketten = 0;
  public bewe = input.required<Bewe>();
  public bewelg = input<Bewelg>();
  public warenuebernahme = input.required<Warenuebernahme>();

  readonly selectableMischPaletten = signal<MischPalette[] | null>(null);
  readonly selectedMischPalette = signal<MischPalette | null>(null);

  readonly mischPalettenService = inject(MischPalettenService);
  private readonly bewegungenService = inject(BewegungenService);
  private readonly config = inject(SOFTLINE_CONFIG_WARENUEBERNAHME);
  private readonly translate = translator();

  private readonly modalFn = modalFn();

  readonly anzahlEtiketten = linkedSignal(() => this.bewe()?.anzahlPalettenMin ?? 0);

  readonly rowsForSplitting = linkedSignal(() => {
    const count = this.anzahlEtiketten();
    const bewelg = this.bewelg();

    const quantity = (bewelg ? bewelg.liefmenge : this.bewe()?.artikelMenge?.menge) ?? 0;

    const rest = quantity % count;
    const amountPerPallet = (quantity - rest) / count;

    return Array.from(Array(count).keys()).map((index, i) => ({
      amount: i === 0 ? amountPerPallet + rest : amountPerPallet,
      index,
    }));
  });

  // keeps SSCC/Charge entered before an abort; the amount follows the current split
  private readonly enteredPallets = linkedSignal<
    { amount: number; index: number }[],
    (PaletteBean | null)[]
  >({
    source: this.rowsForSplitting,
    computation: (rows, previous) =>
      rows.map((row, i) => {
        const pallet = previous?.value[i];
        return pallet ? { ...pallet, menge: row.amount } : null;
      }),
  });

  readonly splittingSum = computed(() =>
    this.rowsForSplitting().reduce((sum, row) => sum + row.amount, 0)
  );

  constructor(private store: Store) {
    super();
  }

  async ngOnInit() {
    const result = await this.mischPalettenService.postionErledigenMischpaletten(
      this.warenuebernahme().id
    );

    this.selectableMischPaletten.set(result.result);
  }

  // set bewegung(bewegung: Bewe) {
  //   this.anzahlEtiketten = bewegung?.anzahlPalettenMin ?? 0;
  //   this.bewe = bewegung;
  // }
  //
  // set warenuebernahme(warenuebernahme: Warenuebernahme) {
  //   this._warenuebernahme = warenuebernahme;
  // }

  async positionErledigen(anzahlEtiketten: number) {
    const split = await this.collectSplit();
    if (!split) return;

    try {
      await this.sendPositionErledigen(anzahlEtiketten, split);
    } catch (e) {
      handleRequestError(e, this.#injector, { show: true });
    }
  }

  private async collectSplit(): Promise<PositionErledigenSplit | null> {
    if (!this.config.palletDetailFlow)
      return { splittung: this.rowsForSplitting().map((row) => row.amount) };
    else if (this.config.palletDetailFlow === 'splitModalOnly') {
      const template = (await this.palletTemplate())?.template;
      if (!template)
        return {
          paletten: this.rowsForSplitting().map((o) => ({
            sscc: '',
            menge: o.amount,
          })),
        };
      const paletten = this.rowsForSplitting().map((o) => ({
        ...template,
        menge: o.amount,
      }));
      return { paletten };
    }

    const paletten = await this.collectPallets();
    return paletten ? { paletten } : null;
  }

  private async collectPallets(): Promise<PaletteBean[] | null> {
    const rows = this.rowsForSplitting();
    if (rows.length === 0) return [];

    // a bewe without a template still gets its pallets captured by hand
    const loaded = await this.palletTemplate();
    if (!loaded) return null;
    const template = loaded.template;

    const bewelg = this.bewelg();
    const unit = bewelg
      ? bewelg.einheit_lf?.arteh
      : this.bewe().artikelMenge?.einheit?.arteh;
    const pallets: PaletteBean[] = [];

    for (const [index, row] of rows.entries()) {
      const result = await this.modalFn({
        id: PalletDetailDialogComponent.MODAL_ID,
        title: this.translate('#WWS_WARENUEBERNAHME.DIALOG.PALLET_DETAIL.TITEL'),
        component: PalletDetailDialogComponent,
        dismiss: true,
        skipInitialInputFocus: true,
        separateContentSection: true,
        data: {
          bewe: this.bewe(),
          palletIndex: index,
          palletCount: rows.length,
          amount: row.amount,
          unit,
          template,
          initial: this.enteredPallets()[index] ?? null,
        },
      });
      if (result === 'DISMISSED') return null;

      pallets.push(result);
      this.enteredPallets.update((entered) =>
        entered.map((pallet, i) => (i === index ? result : pallet))
      );
    }

    const confirmed = await this.modalFn({
      title: this.translate('#WWS_WARENUEBERNAHME.DIALOG.PALLET_SUMMARY.TITEL'),
      component: PalletSummaryDialogComponent,
      dismiss: true,
      separateContentSection: true,
      data: {
        bewe: this.bewe(),
        pallets,
        expectedQuantity:
          (bewelg ? bewelg.liefmenge : this.bewe().artikelMenge?.menge) ?? 0,
        unit,
      },
    });

    return confirmed === true ? pallets : null;
  }

  // one template per bewe, shared by all its pallets; null = the request failed
  private async palletTemplate(): Promise<{ template: PaletteBean | null } | null> {
    try {
      const template = await firstValueFrom(
        this.bewegungenService.paletteTemplate(this.bewe().id)
      );
      return { template };
    } catch (e) {
      handleRequestError(e, this.#injector, { show: true });
      return null;
    }
  }

  private async sendPositionErledigen(
    anzahlEtiketten: number,
    split: PositionErledigenSplit
  ) {
    const bewelg = this.bewelg();

    if (bewelg) {
      const updatedBeweLg = await firstValueFrom(
        this.bewegungenService.positionErledigenLademittel({
          anzahlEtiketten: anzahlEtiketten,
          bewelgBean: bewelg,
          ...split,
        })
      );

      const updatedBeweLgs = [...(this.bewe().bewelgs ?? [])];
      const index = updatedBeweLgs.findIndex(
        (o) => o.id === updatedBeweLg.id && this.bewe().id === updatedBeweLg.idbewe
      );

      if (index < 0) {
        return;
      }

      updatedBeweLgs[index] = { ...updatedBeweLg };

      this.store.commit(
        SOFTLINE_FEATURE_WWS_WARENUEBERNAHME_BEWE,
        WwsBewegungStore.mutations.patch,
        {
          id: this.bewe().id,
          changes: {
            bewelgs: updatedBeweLgs,
          },
        }
      );

      await this.store.dispatch(
        SOFTLINE_FEATURE_WWS_WARENUEBERNAHME_BEWE,
        WwsBewegungStore.actions.loadMany,
        { pathParams: { idwu: this.warenuebernahme().id }, clear: true }
      );

      this.resolve('bewegung');
    } else {
      const result = await this.store.dispatch(
        SOFTLINE_FEATURE_WWS_WARENUEBERNAHME_BEWE,
        WwsBewegungStore.actions.positionErledigen,
        {
          warenuebernahme: this.warenuebernahme(),
          payload: {
            anzahlEtiketten: anzahlEtiketten,
            bewegung: this.bewe(),
            ...split,
          },
        }
      );

      this.resolve(result);
    }
  }

  async positionErledigenMischPaeltte() {
    const palette = this.selectedMischPalette()?.palette;
    if (!palette) return;

    const bewelg = this.bewelg();

    if (bewelg) {
      try {
        const updatedBeweLgPal = await firstValueFrom(
          this.bewegungenService.positionErledigenMischPaletteLademittel({
            palette,
            bewelg,
          })
        );

        const updatedBeweLg = updatedBeweLgPal?.bewelg;

        const updatedBeweLgs = [...(this.bewe().bewelgs ?? [])];
        const index = updatedBeweLgs.findIndex(
          (o) => o.id === updatedBeweLg.id && this.bewe().id === updatedBeweLg.idbewe
        );

        if (index < 0) {
          return;
        }

        updatedBeweLgs[index] = { ...updatedBeweLg };

        this.store.commit(
          SOFTLINE_FEATURE_WWS_WARENUEBERNAHME_BEWE,
          WwsBewegungStore.mutations.patch,
          {
            id: this.bewe().id,
            changes: {
              bewelgs: updatedBeweLgs,
            },
          }
        );

        this.resolve('bewegung');
      } catch (e) {
        handleRequestError(e, this.#injector, { show: true });
      }
    } else {
      const result = await this.store.dispatch(
        SOFTLINE_FEATURE_WWS_WARENUEBERNAHME_BEWE,
        WwsBewegungStore.actions.positionErledigenMischPalette,
        {
          warenuebernahme: this.warenuebernahme(),
          payload: {
            palette: palette,
            bewegung: this.bewe(),
          },
        }
      );
      this.resolve(result);
    }
  }

  changePalletAmount(index: number, amount: number) {
    const array = this.rowsForSplitting();
    array.splice(index, 1, { index, amount });
    this.rowsForSplitting.set([...array]);
  }
}
